import type { LocationIntelDutyDto, LocationIntelOfficerDto } from '@pams/shared';
import { useQuery } from '@tanstack/react-query';
import { Button, Modal } from 'antd';
import { useState, type ReactNode } from 'react';
import { Loading, Skeleton } from '@/components/Skeleton';
import { istDateTime } from '@/lib/analyticsReport';
import { ApiError, api } from '@/lib/api';
import { locationIntelOfficerPath } from '@/lib/locationIntel';
import { queryKeys } from '@/lib/queryKeys';
import { LOCATION_INTEL_COPY } from '../copy';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [LOCATION-INTEL] ONE OFFICER, ON A CLICK — a small dialog, read only when a dot is selected.
 *
 *  ⚠️ THE USER'S TWO RULES, AND WHERE EACH IS HELD:
 *    · THE DOTS CARRY NOBODY'S DETAILS. This read runs only once `officerId` is set — a click on a dot —
 *      and nothing prefetches it on hover or on arrival. The page test asserts no officer request on load.
 *    · NO PHONE, SO NO AUDIT ROW. The DTO has no phone field (the server's schema, pinned by its test),
 *      and this dialog has no row for one.
 *
 *  ⚠️ THE DUTY BLOCK EXISTS ONLY WHEN A DUTY IS IN PROGRESS. `duty: null` is "none now", and then the
 *  dialog says nothing about duty at all — no "Not on duty" line, which would be a claim the map's
 *  colours already refuse to make. When the duty's order is outside the holder's area its places are
 *  withheld (FR-3.6) and the dialog says why, rather than showing an empty row.
 *
 *  ⚠️ A CONTROLLED `Modal`, NEVER `modal.info` — that holder renders above the router and the query
 *  client ([P7C.1 §3]). ONE census site, EXEMPT in `e2e/fixtures/overlays.ts`: it opens from a dot on a
 *  WebGL canvas, which a browser test can reach only with a real Mapbox token and a reported location.
 *
 *  ⚠️ A 404 IS "NO LONGER ON THE MAP", not an error. The server answers one 404 for every officer the map
 *  could not have drawn, and between the map's read and this one an officer can age out, be deactivated or
 *  move out of the holder's area. The dialog says so and points at Refresh — on a RE-OPEN too
 *  ([LOCATION-INTEL.2]): the latest read decides, never the card cached from the first open.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

const copy = LOCATION_INTEL_COPY.card;

const Row = ({ label, children, testId }: { label: string; children: ReactNode; testId?: string }) => (
  <div className="grid grid-cols-[8rem_minmax(0,1fr)] gap-x-3 gap-y-0.5" data-testid={testId}>
    <dt className="text-muted-foreground">{label}</dt>
    <dd className="break-words">{children}</dd>
  </div>
);

const DutyBlock = ({ duty }: { duty: LocationIntelDutyDto }) => (
  <section className="flex flex-col gap-2 border-t border-border-subtle pt-3" data-testid="location-intel-card-duty">
    <h3 className="text-sm font-medium">{copy.dutyHeading}</h3>
    <dl className="flex flex-col gap-1.5 text-sm">
      <Row label={copy.when} testId="location-intel-card-duty-when">
        {duty.slotLabel}
      </Row>
      {duty.placesWithheld ? (
        <p className="text-muted-foreground" data-testid="location-intel-card-duty-withheld">
          {copy.placesWithheld}
        </p>
      ) : (
        <>
          {duty.dutyPlaces.length > 0 && (
            <Row
              label={duty.dutyPlaces.length === 1 ? copy.dutyPlace : copy.dutyPlaces}
              testId="location-intel-card-duty-places"
            >
              {duty.dutyPlaces.join('; ')}
            </Row>
          )}
          {duty.reportingPlace !== null && (
            <Row label={copy.reportingPlace} testId="location-intel-card-duty-reporting">
              {duty.reportingPlace}
            </Row>
          )}
        </>
      )}
    </dl>
  </section>
);

const Details = ({ officer }: { officer: LocationIntelOfficerDto }) => (
  <div className="flex flex-col gap-3" data-testid="location-intel-card">
    <dl className="flex flex-col gap-1.5 text-sm">
      <Row label={copy.rank} testId="location-intel-card-rank">
        {officer.rank?.name ?? copy.none}
      </Row>
      <Row label={copy.unit} testId="location-intel-card-unit">
        {officer.unit?.name ?? copy.none}
      </Row>
      <Row label={copy.division} testId="location-intel-card-division">
        {officer.division?.name ?? copy.none}
      </Row>
      <Row label={copy.lastReported} testId="location-intel-card-last-reported">
        {copy.lastReportedValue(istDateTime(officer.lastReportedAt), officer.band)}
      </Row>
    </dl>
    {officer.duty !== null && <DutyBlock duty={officer.duty} />}
  </div>
);

export const OfficerCard = ({ officerId, onClose }: { officerId: string | null; onClose: () => void }) => {
  /*
   * ⚠️ THE LAST OFFICER SHOWN OUTLIVES THE CLOSE, adjusted during render (React's own pattern for state
   * that follows a prop). The dialog animates out after `officerId` goes back to null; keyed on null it
   * would flash its loading skeleton on the way out.
   */
  const [shown, setShown] = useState(officerId);
  if (officerId !== null && officerId !== shown) setShown(officerId);

  const officer = useQuery({
    queryKey: queryKeys.locationIntel.officer(shown ?? ''),
    queryFn: ({ signal }) => api.get<LocationIntelOfficerDto>(locationIntelOfficerPath(shown ?? ''), { signal }),
    /* ⚠️ THE CLICK IS THE ONLY TRIGGER — the user's rule that a dot pulls no details until selected. */
    enabled: officerId !== null && shown !== null,
  });

  const gone = officer.error instanceof ApiError && officer.error.status === 404;
  /*
   * ⚠️ [LOCATION-INTEL.2] THE LATEST READ DECIDES — AN ERROR IS TESTED BEFORE CACHED DATA. A re-opened card
   * refetches, and TanStack keeps the first read's `data` beside a failed refetch's `error`, so testing `data`
   * first went on showing an officer the server had just refused — aged out, moved out of the holder's area, or
   * the holder's own flag revoked. A Retry keeps the error on screen until a read succeeds.
   */
  const title = officer.isError ? (gone ? copy.goneTitle : copy.errorTitle) : (officer.data?.name ?? copy.loading);

  return (
    <Modal
      open={officerId !== null}
      title={title}
      footer={null}
      width={480}
      centered
      destroyOnHidden
      onCancel={onClose}
    >
      <div className="flex max-h-[65vh] flex-col gap-3 overflow-y-auto pr-1" data-testid="location-intel-card-body">
        {officer.isError ? (
          gone ? (
            <p className="text-sm text-muted-foreground" data-testid="location-intel-card-gone">
              {copy.goneDescription}
            </p>
          ) : (
            <div className="flex flex-col items-start gap-2" data-testid="location-intel-card-error">
              <Button onClick={() => void officer.refetch()}>{copy.retry}</Button>
            </div>
          )
        ) : officer.data !== undefined ? (
          <Details officer={officer.data} />
        ) : (
          <Loading label={copy.loading}>
            <div className="flex flex-col gap-2">
              {[0, 1, 2, 3].map((index) => (
                <Skeleton key={index} className="h-5 w-full" />
              ))}
            </div>
          </Loading>
        )}
      </div>
    </Modal>
  );
};
